import { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiRoute, Route } from 'config';
import useCurrentUser from 'hooks/useCurrentUser';
import useAddresses from 'hooks/useAddresses';
import useUpcomingMenus from 'hooks/useUpcomingMenus';

import { Component as PauseIcon } from 'icons/icon-circle--pause.svg';
import Banner from 'components/shared/Banner';
import { useMenuWithCart } from '../../../../platform/contexts/MenuWithCart';

type Props = {
  onPage?: boolean;
  btnClassName?: string;
};

function ReactivationBanner({
  onPage = false,
  btnClassName,
}: Props): ReactElement {
  const {
    currentUser,
    reload: reloadUser,
  } = useCurrentUser();
  const { reload: reloadMenus } = useUpcomingMenus();
  const { addresses } = useAddresses();
  const hasValidPrimaryAddress = addresses[0]?.isValid;
  const {
    hasValidPaymentMethod,
    isSuspended,
    isReturning,
  } = currentUser;
  const navigate = useNavigate();
  const { reactivateSubscription } = useMenuWithCart();

  const handleReactivateSubscription = async () => {
    if (!hasValidPrimaryAddress) {
      navigate(Route.PROFILE_ADD_ADDRESS);
    } else if (!hasValidPaymentMethod || isSuspended) {
      navigate(Route.PAYMENT_METHOD);
    } else {
      try {
        await reactivateSubscription();
        await reloadUser();
        await reloadMenus();
      } catch (e) {
        navigate(Route.PROFILE_EDIT_PAYMENT);
      }
    }
  };

  let buttonText;
  let bannerBody;

  if (!hasValidPrimaryAddress) {
    buttonText = 'Add Primary Addresses';
    bannerBody = (
      <>
        <strong className="c-heading c-heading--brand-7">
          Your account is not currently active.
        </strong>
        <br />
        Please Edit or Add a valid Primary Address
        to start receiving weekly deliveries.
      </>
    );
  } else if (!hasValidPaymentMethod || isSuspended) {
    buttonText = 'Update payment';
    bannerBody = (
      <>
        <strong className="c-heading c-heading--brand-7">
          Looks like there’s an issue with your
          payment information.
        </strong>
        {
          !onPage && (
            <>
              <br />
              Check your payment information in profile.
            </>
          )
        }
      </>
    );
  } else {
    buttonText = isReturning
      ? 'Resume Orders'
      : 'Reactivate Subscription';

    bannerBody = (
      <>
        <strong className="c-heading c-heading--brand-7">
          We noticed your account is not currently
          active... but it can be!
        </strong>
        <br />
        It only takes one click.
      </>
    );
  }

  return (
    <Banner
      icon={PauseIcon}
      button={(
        <button
          className={`c-button c-button--secondary ${btnClassName}`}
          type="button"
          onClick={handleReactivateSubscription}
        >
          {buttonText}
        </button>
      )}
    >
      {bannerBody}
    </Banner>
  );
}

export default ReactivationBanner;
